import React, { MouseEventHandler, ReactNode, Ref, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import type { FC } from 'react';
import type { IColConfig } from './index';

import { InputCheckbox } from '@nebo-team/vobaza.ui.inputs.input-checkbox/dist';
import { Icon } from '@nebo-team/vobaza.ui.icon/dist';

import styles from './styles.module.scss';

interface ChangeRowsOrderEvent {
  type: string;
  movingItem: any;
  targetItem: any;
}

interface TableRow extends React.RefAttributes<any> {
  className: string;
  beforeRowAddons?: Array<ReactNode>;
  keyField: string;
  data: any;
  cols: IColConfig[];
  gridTemplate: string;
  isSelected?: boolean;
  tableName?: string;
  onClickCheckbox?: (
    keyFieldValue: number | string
  ) => (value: boolean) => void;
  onClickRow?: (keyFieldValue: number | string, value: boolean) => void;
  onChangeRowsOrder?: (event: ChangeRowsOrderEvent) => void;
  withoutGap?: boolean;
  selectable?: boolean;
}

type Props = {
  children: ReactNode;
};

const TableCell: FC<Props> = ({ children }) => {
  return <div className={styles.tableCol}>{children}</div>;
};

const TableRowCheckbox = (props: any) => {
  const { isSelected, onChange } = props;

  return (
    <div className={styles.tableCol}>
      <div className={styles.checkboxWrapper}>
        <InputCheckbox
          label=""
          initialValue={isSelected}
          onChange={onChange}
          variation="tabular"
        />
      </div>
    </div>
  );
};

function TableRowWithSelect(RowComponent: FC<TableRow>) {
  const WrappedRow = React.forwardRef(
    ({ ...props }: TableRow, ref: Ref<any>) => {
      const { keyField, data, isSelected, onClickCheckbox } = props;

      const handleChangeCheckbox =
        onClickCheckbox && onClickCheckbox(data[keyField]);

      const checkbox = (
        <TableRowCheckbox
          key={data[keyField] + '_check'}
          isSelected={isSelected}
          onChange={handleChangeCheckbox}
        />
      );

      props.beforeRowAddons = Array.isArray(props.beforeRowAddons)
        ? [...props.beforeRowAddons, checkbox]
        : [checkbox];

      return <RowComponent ref={ref} {...props} />;
    }
  );

  WrappedRow.displayName = 'TableRowWithSelect';

  return WrappedRow;
}

enum ElementPart {
  UP = 'UP',
  DOWN = 'DOWN',
}

const createIndicator = (part: string): HTMLDivElement => {
  const $el = document.createElement('div');
  const $line = document.createElement('div');

  $el.classList.add(styles.dropIndicator);
  $line.classList.add(styles.dropIndicatorLine);

  if (part) $el.classList.add(styles[`dropIndicator${part}`]);

  $el.appendChild($line);

  return $el;
};

const TableRowDragIcon = React.forwardRef((props, ref: Ref<any>) => {
  return (
    <div ref={ref} className={styles.tableCol}>
      <Icon className={styles.dragIcon} name="DragLines" />
    </div>
  );
});

TableRowDragIcon.displayName = 'TableRowDragIcon';

function TableRowWithDnD(RowComponent: FC<TableRow>) {
  return function WrappedRow({ ...props }: TableRow) {
    const { tableName = '', keyField, data, onChangeRowsOrder } = props;

    const dndType = tableName + '_ROW';

    const dragElemRef = useRef(null);
    const rowElemRef = useRef(null);
    const indicatorRef = useRef<HTMLElement | null>(null);

    const getListItemHoveredPart = (monitor: any) => {
      if (!rowElemRef.current) return;

      const $hoveredEl = rowElemRef.current as HTMLElement;

      const clientOffset = monitor.getClientOffset();
      const hoverBoundingRect = $hoveredEl.getBoundingClientRect();

      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (hoverClientY > hoverMiddleY) return ElementPart.DOWN;

      return ElementPart.UP;
    };

    const resetIndicator = () => {
      indicatorRef.current = null;
      const indicators = document.getElementsByClassName(styles.dropIndicator);

      indicators[0] && indicators[0].remove();
    };

    const [{ isOver }, drop] = useDrop({
      accept: dndType,
      collect(monitor) {
        return {
          isOver: monitor.isOver(),
        };
      },
      hover(item: any, monitor) {
        resetIndicator();
        if (!rowElemRef.current) return;
        // Don't replace items with themselves
        if (item[keyField] === data[keyField]) return;
        const hoveredPart = getListItemHoveredPart(monitor);

        if (!hoveredPart) return;

        const $hoveredEl = rowElemRef.current as HTMLElement;

        if (indicatorRef.current) indicatorRef.current.remove();
        indicatorRef.current = createIndicator(hoveredPart!);

        $hoveredEl.appendChild(indicatorRef.current);
      },
      drop(item: any, monitor) {
        if (!rowElemRef.current || item.id === data.id) return;

        if (!!onChangeRowsOrder) {
          const hoveredPart = getListItemHoveredPart(monitor);
          const type = hoveredPart === ElementPart.UP ? 'BEFORE' : 'AFTER';

          onChangeRowsOrder({
            type: type,
            movingItem: item,
            targetItem: data,
          });
        }
      },
      options: {
        dropEffect: 'copy',
      },
    });

    const [, drag, preview] = useDrag({
      type: dndType,
      item: (): any => {
        return data;
      },
      end: () => {
        resetIndicator();
      },
    });

    drag(dragElemRef);
    drop(preview(rowElemRef));

    const dndIcon = <TableRowDragIcon key="dndIcon" ref={dragElemRef} />;

    props.beforeRowAddons = Array.isArray(props.beforeRowAddons)
      ? [...props.beforeRowAddons, dndIcon]
      : [dndIcon];

    const updatedClasses = [
      props.className || '',
      isOver ? styles.tableRowHovered : '',
    ].join(' ');

    return (
      <RowComponent ref={rowElemRef} {...props} className={updatedClasses} />
    );
  };
}

const TableRow: FC<TableRow> = React.forwardRef<HTMLDivElement, TableRow>(
  (props, ref: Ref<any>) => {
    const {
      className,
      keyField,
      cols,
      data,
      gridTemplate,
      beforeRowAddons,
      isSelected,
      onClickRow,
    } = props;

    const renderCell = (col: IColConfig, index: number) => {
      let content: string | ReactNode = '';

      if (col.render) {
        /** Для таблицы со списком изменений - предыдущее значение выводит красным, новое - зеленым */
        if (col.isPrev) content = col.render(data, styles.prev);
        else if (col.isNext) content = col.render(data, styles.next);
        else content = col.render(data);
      } else if (col.fieldname) content = data[col.fieldname];

      return (
        <TableCell key={`${data[keyField]}-${index}`}>{content}</TableCell>
      );
    };

    const cssClasses = [
      className || '',
      styles.tableRow,
      props.withoutGap ? styles.withoutGap : '',
      isSelected ? styles.isSelected : '',
    ].join(' ');

    const handleClickOnRow: MouseEventHandler<HTMLDivElement> = (e) => {
      if (!(e.target as Element).classList.contains('onClickRow')) return;
      onClickRow && onClickRow(data[keyField], !!isSelected);
    };

    return (
      <div
        ref={ref}
        className={`${cssClasses} onClickRow`}
        style={{
          gridTemplateColumns: gridTemplate,
        }}
        onMouseDown={handleClickOnRow}
      >
        {beforeRowAddons}
        {cols.map(renderCell)}
      </div>
    );
  }
);

TableRow.displayName = 'TableRow';

export { TableCell, TableRow, TableRowWithDnD, TableRowWithSelect };
export type { ChangeRowsOrderEvent };
